import type { ReactNode } from "react";

/**
 * The application frame: header · main · footer.
 *
 * Owns STRUCTURE only — regions, landmarks and slots. Appearance belongs to the design system, which fills the
 * slots without this component changing. It therefore sets no fixed heights or widths a designed header would
 * have to fight.
 *
 * Landmarks: exactly one `banner`, one `main` and one `contentinfo`. `<main id="main">` is the skip-link target
 * declared in index.html.
 *
 * NOTE: `#portal-root` and `#unity-root` live OUTSIDE `#root` in index.html and are deliberately not rendered
 * here — the Unity WebGL iframe must stay out of React's reconciliation, since a remount destroys a live session.
 * When a Unity view becomes primary it takes `role="main"` (keeping its own id) and this component must render
 * without its own `<main>`, so that exactly one main landmark exists.
 */
export function AppShell({
  header,
  footer,
  children,
  renderMain = true,
  mainClassName,
  fillViewport = false,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  /** Set false when another element (e.g. the Unity container) owns the `main` landmark. */
  renderMain?: boolean;
  /**
   * Extra classes on `main` — e.g. `flex flex-col` for a view that must stretch to the footer (the workspace shell,
   * ADR-032): `flex-1` grows `main`, but a child can only fill that growth when `main` is itself a flex column
   * (`min-h-full` can't resolve against a flex-grown block). Off by default: the navigators were built in a block main.
   */
  mainClassName?: string;
  /**
   * Lock the frame to the window, so `main` scrolls inside it instead of the page — for a view that keeps its own
   * scrollbars at the bottom of the window (the PM board's Kanban, ADR-055). Needs `mainClassName` `flex flex-col`.
   */
  fillViewport?: boolean;
}) {
  return (
    // A column that fills the viewport so the footer sits at the bottom on short pages instead of floating
    // mid-screen. `flex-1` on main is what absorbs the slack — this is layout the SHELL owns, distinct from the
    // sizing a designed header brings, which it must not fight.
    <div className={`flex flex-col bg-background text-foreground ${fillViewport ? "h-dvh" : "min-h-dvh"}`}>
      {header}
      {renderMain ? (
        <main id="main" className={`flex-1 ${fillViewport ? "relative min-h-0 overflow-y-auto " : ""}${mainClassName ?? ""}`.trim()}>
          {children}
        </main>
      ) : (
        children
      )}
      {footer}
    </div>
  );
}
